import { createContext, useContext, useState, type ReactNode } from "react";

/**
 * Globaler SlideState — damit die Sidebar auch von Routen außerhalb
 * /present (also /workshop/*, /admin, /showcase) auf den aktuell gewählten
 * Slide zeigen + Springen kann. PresenterPage spiegelt seinen lokalen Index
 * via setSlideIndex/setSlideTotal in den Context.
 */

interface SlideContextValue {
  index: number;
  total: number;
  /** Setzt den Index (lokal in PresenterPage) und navigiert zu /present. */
  setIndex: (i: number) => void;
  /** Updates from PresenterPage. */
  reportIndex: (i: number) => void;
  reportTotal: (n: number) => void;
}

const SlideContext = createContext<SlideContextValue | null>(null);

export function SlideStateProvider({ children }: { children: ReactNode }) {
  const [index, setLocalIndex] = useState(0);
  const [total, setTotal] = useState(30);
  const value: SlideContextValue = {
    index,
    total,
    setIndex: (i: number) => {
      setLocalIndex(i);
      // Routing macht der Aufrufer (z.B. Sidebar) per navigate("/present");
      // Sidebar setzt zusätzlich window.location als Fallback.
    },
    reportIndex: (i: number) => setLocalIndex(i),
    reportTotal: (n: number) => setTotal(n),
  };
  return <SlideContext.Provider value={value}>{children}</SlideContext.Provider>;
}

export function useSlideState() {
  const ctx = useContext(SlideContext);
  if (!ctx) {
    return {
      index: 0,
      total: 30,
      setIndex: () => undefined,
      reportIndex: () => undefined,
      reportTotal: () => undefined,
    } satisfies SlideContextValue;
  }
  return ctx;
}
